// ============================================================================
// CaseStatusSection — the reusable Case Status Update widget UI
// ============================================================================
// Renders a materialised case_status section (see caseStatusWidget.ts) as a
// clean proceedings log: a header, the repeatable "updates" grid (card rows via
// <DynamicGrid>), and — when the config enables `award` and the proceedings
// reach the awarded state — an inline Award Details block.
//
// It renders the SAME field specs the validation engine sees (DynamicForm
// materialises the spec first), so validation + values plumbing keep working.
// This component only owns layout + the awarded-state reveal.

import React, { useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type {
  CaseStatusUpdateConfig,
  FieldSpec,
  FormValues,
  GridFieldSpec,
  GridRowValues,
  SectionSpec,
} from 'api/types/formSpec.types';
import DynamicField from './DynamicField';
import DynamicGrid from './DynamicGrid';
import type { SeedContext } from './seedResolver';

export interface CaseStatusSectionProps {
  section: SectionSpec;              // already materialised (fields[] + case_status_update)
  values: FormValues;
  onFieldChange: (key: string, val: unknown) => void;
  errors: Record<string, string>;
  disabled?: boolean;
  seedContext?: SeedContext;
}

const UPDATES_KEY = 'status_updates';
const AWARD_KEYS = new Set(['award_date', 'award_dispatch_date', 'award_amount', 'award_remarks', 'next_action_type']);
const DEFAULT_AWARDED_VALUE = 'AWARDED';

const CaseStatusSection: React.FC<CaseStatusSectionProps> = ({
  section, values, onFieldChange, errors, disabled, seedContext,
}) => {
  const cfg: CaseStatusUpdateConfig = section.case_status_update ?? {};
  const awardedValue = cfg.awarded_value ?? DEFAULT_AWARDED_VALUE;

  // Bucket the materialised fields into the updates grid + award scalars.
  const { updatesField, awardFieldList } = useMemo(() => {
    let updates: GridFieldSpec | null = null;
    const award: FieldSpec[] = [];
    for (const f of section.fields ?? []) {
      if (f.key === UPDATES_KEY && f.type === 'grid') updates = f as GridFieldSpec;
      else if (AWARD_KEYS.has(f.key)) award.push(f);
    }
    return { updatesField: updates, awardFieldList: award };
  }, [section.fields]);

  const rows = Array.isArray(values[UPDATES_KEY]) ? (values[UPDATES_KEY] as GridRowValues[]) : [];
  const awarded = cfg.award === true && rows.some((r) => r?.next_posting === awardedValue);
  const awardComplete = awarded && !!values['award_date'] && values['award_amount'] != null && values['award_amount'] !== '';

  return (
    <div style={cardStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <IconSystem.calendar size={16} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: '0.9rem', fontWeight: 600 }}>{section.title ?? 'Case Status Updates'}</div>
          {section.description && (
            <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginTop: '0.1rem' }}>
              {section.description}
            </div>
          )}
        </div>
      </div>

      {/* Proceedings log */}
      <div style={{ padding: '0.85rem' }}>
        {updatesField && (
          <DynamicGrid
            field={updatesField}
            value={values[UPDATES_KEY] as GridRowValues[] | null | undefined}
            onChange={(next) => onFieldChange(UPDATES_KEY, next)}
            error={errors[UPDATES_KEY]}
            disabled={disabled}
            seedContext={seedContext}
          />
        )}

        {/* Inline Award — revealed once a posting is marked Awarded */}
        {awarded && awardFieldList.length > 0 && (
          <div style={awardBlockStyle}>
            <div style={awardHeaderStyle}>
              <IconSystem.check size={14} />
              Award Details
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.6rem 0.85rem' }}>
              {awardFieldList.map((f) => (
                <DynamicField
                  key={f.key}
                  field={f}
                  value={values[f.key]}
                  onChange={(v) => onFieldChange(f.key, v)}
                  error={errors[f.key]}
                  disabled={disabled}
                  allValues={values}
                  seedContext={seedContext}
                />
              ))}
            </div>
            {awardComplete && (
              <div style={hintStyle}>
                <IconSystem.info size={13} style={{ flexShrink: 0 }} />
                Award recorded. You can now close the case from the action bar (Filing → Closure).
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const cardStyle: React.CSSProperties = {
  border: '1px solid var(--border-color)', borderRadius: 8,
  background: 'var(--bg-primary)', overflow: 'hidden',
};
const headerStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: '0.5rem',
  padding: '0.6rem 0.85rem', borderBottom: '1px solid var(--border-color)',
  background: 'var(--bg-secondary)',
};
const awardBlockStyle: React.CSSProperties = {
  marginTop: '0.9rem', padding: '0.75rem', borderRadius: 8,
  border: '1px solid #16a34a', background: '#16a34a0d',
};
const awardHeaderStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: '0.4rem',
  fontSize: '0.8rem', fontWeight: 700, color: '#166534', marginBottom: '0.6rem',
};
const hintStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: '0.4rem',
  marginTop: '0.6rem', fontSize: '0.75rem', color: '#166534',
};

export default CaseStatusSection;
